import React from "react";
import { logger } from "@/lib/logger";
import { isChunkLoadError, shouldRetryChunkLoad } from "@/utils/chunkLoadRecovery";

interface Props {
    children: React.ReactNode;
}

interface State {
    failed: boolean;
    chunkLoadFailure: boolean;
}

export class ChunkLoadErrorBoundary extends React.Component<Props, State> {
    state: State = { failed: false, chunkLoadFailure: false };

    static getDerivedStateFromError(error: unknown): State {
        return { failed: true, chunkLoadFailure: isChunkLoadError(error) };
    }

    componentDidCatch(error: unknown): void {
        if (!isChunkLoadError(error)) {
            logger.error("ui.render.failed", error);
            return;
        }

        const pathname = window.location.pathname;
        logger.warn("deployment.chunk.stale", { pathname });
        if (shouldRetryChunkLoad(pathname)) {
            const nextUrl = new URL(window.location.href);
            nextUrl.searchParams.set("__chunk_retry", String(Date.now()));
            window.location.replace(nextUrl.toString());
        }
    }

    private retry = () => {
        window.location.reload();
    };

    private returnToTVHome = () => {
        window.location.assign("/tv");
    };

    render() {
        if (!this.state.failed) return this.props.children;

        return (
            <main className="flex min-h-[100dvh] items-center justify-center bg-background px-4 text-foreground">
                <div className="w-full max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
                    <h1 className="text-xl font-semibold">
                        {this.state.chunkLoadFailure ? "Update available" : "QitPlaylist could not continue"}
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        {this.state.chunkLoadFailure
                            ? "QitPlaylist was updated while this page was open. Reload to use the latest version."
                            : /^\/tv(?:\/|$)/.test(window.location.pathname)
                                ? "The TV player encountered an unexpected error. Reload the app and try again."
                                : "The application encountered an unexpected error. Reload and try again."}
                    </p>
                    <button
                        className="mt-5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                        onClick={this.retry}
                        type="button"
                    >
                        {this.state.chunkLoadFailure ? "Load update" : "Reload QitPlaylist"}
                    </button>
                    {!this.state.chunkLoadFailure && /^\/tv(?:\/|$)/.test(window.location.pathname) ? (
                        <button
                            className="ml-3 mt-5 rounded-md border border-border px-4 py-2 text-sm font-medium"
                            onClick={this.returnToTVHome}
                            type="button"
                        >
                            Return to TV home
                        </button>
                    ) : null}
                </div>
            </main>
        );
    }
}
